<template>
  <div v-if="!isEmptyData || pageLoading" class="hiring-report">
    <InsightHeader
      class="header"
      :loading="pageLoading"
      :reporting-message="reportingMessage"
      @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
    >
      <template #header:bottom>
        <div
          :class="{ 'split-banner': showTwoBanners }"
        >
          <InsightMessageBanner
            v-if="showOfferEndpointWarningBanner"
            :header="offerEndpointWarning.header"
            :message="offerEndpointWarning.message"
            :beacon-article-id="offerEndpointWarning.beaconArticleId"
            :data-png-remove="offerEndpointWarning.dataPngRemove"
            :warning-red="true"
            @close="showOfferEndpointWarningBanner = false;"
          />
          <InsightMessageBanner
            :header="reportingMessage.header"
            :message="reportingMessage.message"
            :beacon-article-id="reportingMessage.beaconArticleId"
            :data-png-remove="reportingMessage.dataPngRemove"
            @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
          />
        </div>
      </template>
    </InsightHeader>
    <div class="hiring-report-content">
      <HiringReportSummary
        class="summary mb-0"
        :loading="pageLoading"
        :data="data"
        :tooltips="tooltips"
        :project-name="projectName"
        :date-range="dateRange"
      />
      <HiringReportAcceptanceRateWidget
        class="hiring-acceptance-rate"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <HiringReportTimeToHireWidget
        class="hiring-time-to-hire"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <HiringReportGenderDistributionWidget
        class="hiring-gender-distribution"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <HiringReportSourceCategoriesWidget
        class="hiring-source-categories"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <HiringReportTable
        class="table"
        :error="error"
        :loading="pageLoading"
        :empty-data="isEmptyData"
        :data="data"
        :table-rows="tableRows"
        :downloading="downloading"
        @download-csv="downloadHiringTable"
      />
    </div>
    <FilterExportPreview class="mt-4" />
  </div>
  <NoDataForWidget v-else />
</template>

<script setup>
import {
  onMounted, onBeforeUnmount, computed, ref, watch,
} from 'vue';
import { useFilters } from '@/stores/filters/filters';
import { useRoute, useRouter } from 'vue-router';
import { useAppLoading } from '@/stores/app/loading';
import { storeToRefs } from 'pinia';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useAppBase } from '@/stores/app/base';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import NoDataForWidget from '@/components/insights/NoDataForWidget.vue';
import { useHiringReport } from '@/stores/insights/hiringReport';
import HiringReportAcceptanceRateWidget from '@/components/insights/ProcessInsights/HiringReport/HiringReportAcceptanceRateWidget.vue';
import HiringReportTimeToHireWidget from '@/components/insights/ProcessInsights/HiringReport/HiringReportTimeToHireWidget.vue';
import HiringReportGenderDistributionWidget from '@/components/insights/ProcessInsights/HiringReport/HiringReportGenderDistributionWidget.vue';
import HiringReportSourceCategoriesWidget from '@/components/insights/ProcessInsights/HiringReport/HiringReportSourceCategoriesWidget.vue';
import HiringReportTable from '@/components/insights/ProcessInsights/HiringReport/HiringReportTable.vue';
import HiringReportSummary from '@/components/insights/ProcessInsights/HiringReport/HiringReportSummary.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useInsights } from '@/stores/insights/insights';
import { useTooltips } from '@/stores/insights/tooltips';
import InsightHeader from '@/components/insights/InsightHeader.vue';
import { hiringOfferEndpointWarning } from '@/helpers/constants/reportingWarnings';
import InsightMessageBanner from '@/components/insights/InsightMessageBanner.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';
import { useTracking } from '@/stores/tracking/tracking';
import FilterExportPreview from '@/components/insights/ExportModal/FilterExportPreview.vue';

const emit = defineEmits(['track-page-load']);

const {
  isEmptyData, data, tableRows, error, flags,
} = storeToRefs(useHiringReport());
const { pageLoading } = storeToRefs(useAppLoading());
const { authData } = storeToRefs(useAppAuthData());
const { reportingMessage } = storeToRefs(useInsights());
const { tooltips } = storeToRefs(useTooltips());
const { dateRange } = storeToRefs(useDatePicker());

const { loadFilters, setFilterConfig } = useFilters();
const { setDatePickerVariant, resetDateRange } = useDatePicker();
const { loadHiringReport, downloadHiringCSV } = useHiringReport();
const { showFilterBarSettings } = useAppBase();
const { setReportingMessage } = useInsights();
const { setExportConfig, resetExportConfig } = useInsightsExport();
const { setShareConfig, resetShareConfig } = useInsightsShare();

const route = useRoute();
const router = useRouter();

const showOfferEndpointWarningBanner = ref(false);
const offerEndpointWarning = ref({ header: '', message: '', link: '' });
const downloading = ref(false);

const projectName = computed(() => authData.value.projectName);

const showTwoBanners = computed(() => flags.value.applyWarning
  && Boolean(reportingMessage.value.message?.length));

const loadPage = async (params) => Promise.all([
  loadFilters(params),
  loadHiringReport(params),
]);

const downloadHiringTable = async () => {
  downloading.value = true;

  await downloadHiringCSV({
    dateRange: dateRange.value,
    projectName: projectName.value,
  });

  useTracking().insightsDownloadReport({
    reportName: route.name,
    downloadType: 'Individual Widgets',
    downloadLocation: 'Individual Widget',
    fileType: 'CSV',
    widgetName: 'Data Table (.csv)',
  });

  downloading.value = false;
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  emit('track-page-load', { page: 'Hiring Report' });

  setFilterConfig({
    module: 'insights',
    endpoint: 'process',
    datepicker: true,
    narrow: true,
    showMyJobs: true,
  });

  setDatePickerVariant('multiOption');
  showFilterBarSettings({ route: route.name, options: [] });

  setShareConfig({
    shareable: true,
    showReportImageButton: true,
  });

  setExportConfig({
    csvExport: true,
    pngExport: true,
    pngWidgetOptions: {
      number_of_hires: 'Metric: Number of Hires',
      avg_acceptance_rate: 'Metric: Offer Acceptance Rate',
      median_hire_time: 'Metric: Time to Hire',
      current_open_offers: 'Metric: Open Offers',
      'offer-acceptance-rate-vs-benchmark': 'Benchmark: Offer Acceptance',
      'time-to-hire-vs-benchmark': 'Benchmark: Time to Hire',
      'gender-distribution-of-hires': 'Graph: Gender Distribution of Hires',
      'hire-source-category': 'Graph: Source Distribution of Hires',
      'hiring-report-table': 'Table: Offer Data',
    },
    csvOptions: [
      {
        label: 'Data Table (.csv)',
        csvDownloadFunction: () => downloadHiringCSV({
          dateRange: dateRange.value,
          projectName: projectName.value,
        }),
      },
    ],
  });

  await loadPage({ routeQuery: route.query || '', selectedMyJobs: route.query?.owned === 'true', router });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  resetDateRange();

  resetExportConfig();

  resetShareConfig();
});

watch(
  () => flags.value.applyWarning,
  (value) => {
    if (value) {
      showOfferEndpointWarningBanner.value = true;
      offerEndpointWarning.value = hiringOfferEndpointWarning;
    } else {
      showOfferEndpointWarningBanner.value = false;
    }
  },
  { immediate: true },
);
</script>

<style lang="less" scoped>
.hiring-report {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding-bottom: 16px;
}

.hiring-report-content {
  width: 100%;
  display: grid;
  grid-template-areas:
    "summary summary"
    "hiringAcceptanceRate hiringTimeToHire"
    "hiringGenderDistribution hiringSourceCategories"
    "table table";
  grid-template-rows: auto 188px 1fr auto;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.summary {
  grid-area: summary;
}
.hiring-acceptance-rate {
  grid-area: hiringAcceptanceRate;
}
.hiring-time-to-hire {
  grid-area: hiringTimeToHire;
}
.hiring-source-categories {
  grid-area: hiringSourceCategories;
}
.hiring-gender-distribution {
  grid-area: hiringGenderDistribution;
}
.table {
  grid-area: table;
}

.split-banner {
  display: flex;
  justify-content: space-between;
}
</style>
